import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import { capResults, shapeResult } from './helpers';
import {
    ACTIVITY_EMPTY,
    OPPONENT_LINK_TARGET,
    OPPONENT_LINK_REL,
    SINGLES_ICON,
    DOUBLES_ICON,
} from './constants';
import styles from './playersList.scss';

const WIN_LABEL = 'W';
const LOSS_LABEL = 'L';
const VS_LABEL = 'vs.';
const OPPONENT_SEPARATOR = ' / ';

// Recent Results cell (desktop cell + mobile expanded section). Up to two results — one Singles, one
// Doubles. Each result is a plain (non-link) date · W/L · score line over a match line: a 1-person icon
// (singles) or 2-person icon (doubles), "vs.", and the opponent name(s) — the only links here. Doubles
// shows the opponent team (both links); the opponent UTR after each name matches the result's match
// type. Opponent links open the profile in a new tab. No activity of this kind → `—`.
const RecentResultsCell = ({ results, 'data-testid': dataTestId }) => {
    const shaped = capResults(results).map(shapeResult);

    if (shaped.length === 0) {
        return (
            <div className={`${styles.activityCell} ${styles.activityCellEmpty}`} data-testid={dataTestId || undefined}>
                <span className={styles.activityEmpty} data-testid={dataTestId && `${dataTestId}.empty`}>
                    {ACTIVITY_EMPTY}
                </span>
            </div>
        );
    }

    return (
        <div className={styles.activityCell} data-testid={dataTestId || undefined}>
            {shaped.map((result, index) => {
                const rowTestId = dataTestId && `${dataTestId}.result-${index}`;
                return (
                    <div key={result.isDoubles ? 'doubles' : 'singles'} className={styles.resultRow} data-testid={rowTestId || undefined}>
                        <div className={styles.resultLine}>
                            <span className={styles.resultDate} data-testid={rowTestId && `${rowTestId}.date`}>
                                {result.date}
                            </span>
                            <span
                                className={`${styles.resultOutcome} ${result.isWin ? styles.resultWin : styles.resultLoss}`}
                                data-testid={rowTestId && `${rowTestId}.outcome`}
                            >
                                {result.isWin ? WIN_LABEL : LOSS_LABEL}
                            </span>
                            <span className={styles.resultScore} data-testid={rowTestId && `${rowTestId}.score`}>
                                {result.score}
                            </span>
                        </div>
                        <div className={styles.resultMatch}>
                            <img className={styles.resultIcon} src={result.isDoubles ? DOUBLES_ICON : SINGLES_ICON} alt="" data-testid={rowTestId && `${rowTestId}.match-icon`} />
                            <span className={styles.resultVs} data-testid={rowTestId && `${rowTestId}.vs`}>
                                {VS_LABEL}
                            </span>
                            {result.opponents.map((opponent, opponentIndex) => (
                                <Fragment key={opponent.playerId ?? opponent.name}>
                                    {opponent.profileUrl ? (
                                        <a
                                            className={styles.opponentLink}
                                            href={opponent.profileUrl}
                                            target={OPPONENT_LINK_TARGET}
                                            rel={OPPONENT_LINK_REL}
                                            data-testid={rowTestId && `${rowTestId}.opponent-${opponentIndex}`}
                                        >
                                            {opponent.name}
                                        </a>
                                    ) : (
                                        <span
                                            className={styles.opponentName}
                                            data-testid={rowTestId && `${rowTestId}.opponent-${opponentIndex}`}
                                        >
                                            {opponent.name}
                                        </span>
                                    )}
                                    {opponent.rating && (
                                        <span
                                            className={styles.opponentRating}
                                            data-testid={rowTestId && `${rowTestId}.opponent-${opponentIndex}.rating`}
                                        >
                                            {opponent.rating}
                                        </span>
                                    )}
                                    {opponentIndex < result.opponents.length - 1 && (
                                        <span
                                            className={styles.opponentSeparator}
                                            data-testid={rowTestId && `${rowTestId}.opponent-${opponentIndex}.separator`}
                                        >
                                            {OPPONENT_SEPARATOR}
                                        </span>
                                    )}
                                </Fragment>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

RecentResultsCell.propTypes = {
    results: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
    'data-testid': PropTypes.string,
};

RecentResultsCell.defaultProps = {
    results: [],
    'data-testid': null,
};

export default RecentResultsCell;
